<template>
  <div class="sticky">
    <div class="sidebar-categories">
      <div class="product-category mb-4">
        <p class="label">{{$t('common.productCategory')}}</p>
        <div v-if="selectedProductCategory">
          <div class="selected" v-b-toggle.collapse-category>
            <span>
              <span class="bold">{{selectedProductCategory.acf.short_name}}</span> <span v-html="selectedProductCategory.name"></span>
            </span>
            <span class="change">{{$t('common.change')}}&nbsp;></span>
          </div>
          <div class="available-items">
            <b-collapse id="collapse-category">
              <template v-for="(category, i) in availableCategories">
                <div v-if="selectedProductCategory && (category.id !== selectedProductCategory.id)" :key="`category-${i}`"
                    class="category"
                    @click="selectProductCategory(category)">
                  <span>
                    <span class="bold">{{category.acf.short_name}}</span>&nbsp;
                    <span v-html="category.name"></span>
                  </span>
                </div>
              </template>
            </b-collapse>
          </div>
        </div>
        <div v-else class="available-items">
          <div v-for="(category, i) in availableCategories" :key="`category-${i}`">
            <div class="category"
                @click="selectProductCategory(category)">
              <span>
                <span class="bold">{{category.acf.short_name}}</span>&nbsp;
                <span v-html="category.name"></span>
              </span>
            </div>
          </div>
        </div>
      </div>
      <div v-if="availableGroups" class="product-group mb-4">
        <p class="label">{{$t('common.productGroup')}}</p>
        <div class="text-center py-5" v-if="groupsLoading">
          <b-spinner type="grow" variant="secondary"></b-spinner>
        </div>
        <div v-else>
          <div v-if="selectedProductGroup">
            <div class="selected" v-b-toggle.collapse-group>
              <span>{{selectedProductGroup.name}}</span>
              <span class="change">{{$t('common.change')}}&nbsp;></span>
            </div>
            <div class="available-items">
              <b-collapse id="collapse-group">
                <template v-for="(group, i) in availableGroups">
                  <div v-if="selectedProductGroup && group.id !== selectedProductGroup.id" :key="`group-${i}`"
                      class="category"
                      @click="selectProductGroup(group)">
                    {{group.name}}
                  </div>
                </template>
              </b-collapse>
            </div>
          </div>
          <div v-else>
            <div v-if="availableGroups.length" class="available-items">
              <template v-for="(group, i) in availableGroups">
                <div :key="`group-${i}`"
                    class="category"
                    @click="selectProductGroup(group)">
                  {{group.name}}
                </div>
              </template>
            </div>
            <div v-else>
              <no-content :msg="$t('common.noMatchingGroups')"></no-content>
            </div>
          </div>
        </div>
      </div>
      <div class="material-type" v-if="availableMaterialTypes">
        <p class="label">{{$t('common.materialType')}}</p>
        <div class="available-types">
          <div v-for="(type, i) in availableMaterialTypes" :key="`type-${i}`">
            <div class="text-uppercase type"
                 @click="selectedProductGroup && (!selectedMaterialType || selectedMaterialType.id !== type.id)
                         ? selectMaterialType(type) : false"
                 :class="{'pointer': selectedProductGroup && (!selectedMaterialType || selectedMaterialType.id !== type.id)}">
              <span :class="{'bold': selectedMaterialType && selectedMaterialType.id === type.id}">{{type.name}}</span>
              <div>
                <img src="@/assets/icons/arrow-primary.svg"
                    v-if="selectedMaterialType && selectedMaterialType.id === type.id">
                <img v-else src="@/assets/icons/plus-secondary-thin.svg">
              </div>
            </div>
            <b-collapse :visible="selectedMaterialType && selectedMaterialType.id === type.id" :id="`collapse-type-${type.id}`">
              <div v-if="selectedMaterialType && selectedMaterialType.id === type.id" class="available-materials">
                <div class="text-center py-2" v-if="materialsLoading">
                  <b-spinner small type="grow" variant="secondary"></b-spinner>
                </div>
                <div v-else>
                  <div v-if="availableMaterials.length">
                    <div v-for="(material, i) in availableMaterials" :key="`material-${i}`"
                          class="material text-uppercase">
                      <div v-if="currentMaterialId === material.id" class="bold">
                        <span v-html="material.title.rendered"></span>
                      </div>
                      <localized-link v-else :to="{name: 'Material', params: {
                          lang: $i18n.locale,
                          slug: decodeURIComponent(material.slug),
                          productCategory: selectedProductCategory,
                          productGroup: selectedProductGroup,
                          materialType: selectedMaterialType
                        }}">
                        <span v-html="material.title.rendered"></span>
                      </localized-link>
                    </div>
                  </div>
                  <div v-else class="my-2 no-materials">
                    {{$t('common.noMaterials')}}
                  </div>
                </div>
              </div>
            </b-collapse>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import NoContent from './NoContent.vue';

export default {
  components: { NoContent },
  props: {
    productCategory: null,
    productGroup: null,
    materialType: null,
    currentMaterialId: null,
  },
  data() {
    return {
      selectedProductCategory: this.productCategory,
      selectedProductGroup: this.productGroup,
      selectedMaterialType: this.materialType,
      availableCategories: null,
      availableGroups: null,
      availableMaterialTypes: null,
      availableMaterials: null,
      groupsLoading: false,
      materialsLoading: false,
    };
  },
  methods: {
    getProductCategories() {
      this.$http.get(`${process.env.VUE_APP_API_URL}/material_categories?lang=${this.$i18n.locale}&parent=0`)
        .then((response) => {
          this.availableCategories = response.data;
        });
    },
    getProductGroups(id) {
      this.groupsLoading = true;
      this.$http.get(`${process.env.VUE_APP_API_URL}/material_categories?lang=${this.$i18n.locale}&parent=${id}&hide_empty=true`)
        .then((response) => {
          this.groupsLoading = false;
          this.availableGroups = response.data;
        });
    },
    getMaterialTypes(id) {
      this.$http.get(`${process.env.VUE_APP_API_URL}/material_categories?lang=${this.$i18n.locale}&parent=${id}&hide_empty=true`)
        .then((response) => {
          this.availableMaterialTypes = response.data;
        });
    },
    getAvailableMaterials() {
      this.materialsLoading = true;
      const paramMaterialType = `material_categories=${this.selectedMaterialType.id}`;
      this.$http.get(`${process.env.VUE_APP_API_URL}/materials?lang=${this.$i18n.locale}&${paramMaterialType}`)
        .then((response) => {
          this.availableMaterials = response.data;
          this.materialsLoading = false;
        });
    },
    selectProductCategory(category) {
      this.selectedProductCategory = category;
      this.$root.$emit('bv::toggle::collapse', 'collapse-category');
      this.selectedProductGroup = null;
      this.selectedMaterialType = null;
      this.selectedMaterialType = null;
      this.availableMaterialTypes = null;
      this.getProductGroups(category.id);
    },
    selectProductGroup(group) {
      this.selectedProductGroup = group;
      this.getMaterialTypes(group.id);
      this.$root.$emit('bv::toggle::collapse', 'collapse-group');
    },
    selectMaterialType(type) {
      this.selectedMaterialType = type;
      this.getAvailableMaterials();
      this.$root.$emit('bv::toggle::collapse', `collapse-type-${type.id}`);
    },
  },
  mounted() {
    this.getProductCategories();
    if (this.selectedProductCategory && this.selectedProductGroup) {
      this.getProductGroups(this.selectedProductCategory.id);
      this.getMaterialTypes(this.selectedProductGroup.id);
      this.getAvailableMaterials();
    }
  },
};
</script>
<style lang="scss" scoped>
@import '@/assets/scss/common.scss';
.sidebar-categories {
  font-size: 13px;
  .label {
    color: $secondary;
    text-transform: uppercase;
    font-size: 11px;
    margin-bottom: .5rem;
  }
  .selected {
    text-transform: uppercase;
    color: #fff;
    padding: .8rem 1.4rem;
    cursor: pointer;
    border-radius: 7px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    &:focus {
      outline: 0;
    }
    .change {
      font-size: 10px;
      white-space: nowrap;
    }
  }
  .available-items {
    background: $light-grey;
    .category {
      padding: .8rem 1.4rem;
      border-bottom: 1px solid white;
      text-transform: uppercase;
      cursor: pointer;
    }
  }
}
.product-category {
  .selected {
    background: $secondary;
  }
}
.product-group {
  .selected {
    background: $primary;
  }
}
.material-type {
  .available-types {
    .type {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: .4rem 0;
      img {
        max-width: 13px;
        transform: rotate(90deg);
        margin-right: 2rem;
      }
    }
    .available-materials {
      margin-bottom: .5rem;
      padding-bottom: .7rem;
      border-bottom: 1px solid $secondary;
      .material {
        padding: .3rem 0 .3rem 1.5rem;
      }
      .no-materials {
        color: #909090;
      }
    }
  }
}
@include media-breakpoint-down(lg) {
  .sidebar-categories {
    font-size: 11px;
    .label {
      font-size: 9px;
    }
    .change {
      font-size: 8px!important;
    }
  }
}
</style>
